import { z } from "zod";

import { MetaDataSchema } from "@/schemas/meta-data";

export const SubscriptionTypeSchema = z.enum([
	"FREE",
	"FREE_TRIAL",
	"PAID",
	"PAID_TRIAL",
	"FREE_EXPIRED",
]);

export const BillingIntervalSchema = z.enum([
	"MONTHLY",
	"EVERY_3_MONTHS",
	"EVERY_6_MONTHS",
	"YEARLY",
]);

/** PREC-4336 part C: product events per complete-UTC-day window; null is unmeasured. */
export const ActivityWindowStatsSchema = z.object({
	day: z.number().nullable(),
	week: z.number().nullable(),
	month: z.number().nullable(),
});

export const FeatureActivityStatsSchema = ActivityWindowStatsSchema.extend({
	/** Catalog feature key (labelled from the shared product-feature catalog). */
	feature: z.string(),
});

export const ActivityStatsSchema = ActivityWindowStatsSchema.extend({
	features: z.array(FeatureActivityStatsSchema),
});

export const SupportActivityWindowSchema = z.object({
	from: z.string(),
	to: z.string(),
});

export const SupportOpenScrapingRequestSchema = z.object({
	id: z.number(),
	name: z.string(),
	status: z.string(),
});

export const SupportRunningCampaignSchema = z.object({
	campaignId: z.string().nullable(),
	listId: z.number(),
	listName: z.string(),
	emailsSentToday: z.number(),
	linkedinInvitesToday: z.number(),
	linkedinMessagesToday: z.number(),
});

export const SEND_ERROR_RANGES = ["TODAY", "YESTERDAY", "LAST_7_DAYS", "LAST_30_DAYS"] as const;

export const SendErrorRangeSchema = z.enum(SEND_ERROR_RANGES);

export type SendErrorRange = z.infer<typeof SendErrorRangeSchema>;

export const DEFAULT_SEND_ERROR_RANGE: SendErrorRange = "LAST_30_DAYS";

export const SupportSendErrorsSummarySchema = z.object({
	total: z.number(),
	needsAttention: z.number(),
	recipientSide: z.number(),
	inmailUndelivered: z.number(),
});

export const SupportSendErrorSchema = z.object({
	id: z.number(),
	prospectId: z.number().optional(),
	listId: z.number(),
	listName: z.string().optional(),
	campaignName: z.string().optional(),
	prospectName: z.string().optional(),
	prospectTitle: z.string().optional(),
	organizationName: z.string().optional(),
	stepType: z.string(),
	stepLabel: z.string(),
	category: z.string(),
	needsAttention: z.boolean(),
	isInmailUndelivered: z.boolean(),
	reason: z.string(),
	rawError: z.string().optional(),
	failedAt: z.string(),
});

export const SupportSendErrorsListSchema = z.array(SupportSendErrorSchema);

export const SupportSendErrorResolvedSchema = z.object({
	id: z.number(),
	errorResolvedAt: z.string(),
});

export const SupportSendErrorsOverviewSchema = z.object({
	totalErrors24h: z.number(),
	needsAttention24h: z.number(),
	affectedCompanies: z.number(),
	inmailUndelivered24h: z.number(),
	disconnectedAccounts: z.number(),
	stepsSentOk24h: z.number(),
	windowHours: z.number(),
});

export const SupportCompanySchema = z.object({
	id: z.number(),
	name: z.string(),
	/** PREC-4336 part C: latest UTC day with a product event (YYYY-MM-DD), never a time. */
	lastActivityDate: z.string().nullable(),
	/** PREC-4335: the latest `user.lastActiveAt` of the company's users. */
	lastActiveAt: z.string().nullable().optional(),
	hasOpenScrapingRequest: z.boolean(),
	openScrapingRequests: z.array(SupportOpenScrapingRequestSchema),
	hasRunningCampaign: z.boolean(),
	runningCampaignCount: z.number(),
	runningCampaigns: z.array(SupportRunningCampaignSchema),
	messagesToday: z.number(),
	activityStats: ActivityStatsSchema,
	subscriptionType: SubscriptionTypeSchema,
	billingInterval: BillingIntervalSchema.nullable().optional(),
	creditsPerCommitmentPeriod: z.number().nullable().optional(),
	availableCredits: z.number(),
	creditAllowance: z.number(),
	lowOnCredits: z.boolean(),
	hasSendErrors: z.boolean(),
	sendErrors: SupportSendErrorsSummarySchema,
});

export const SupportCompaniesResponseSchema = z.object({
	data: z.array(SupportCompanySchema),
	meta: MetaDataSchema,
	/** Newest UTC day the nightly rollup completed; null before the first rollup. */
	lastRolledUpDate: z.string().nullable(),
	/** The 30-day window the activity figures cover; null before the first rollup. */
	activityWindow: SupportActivityWindowSchema.nullable(),
});

export const LINKEDIN_CONNECTION_TYPES = ["SALES_NAVIGATOR", "PREMIUM", "NORMAL"] as const;

export const LinkedinConnectionTypeSchema = z.enum(LINKEDIN_CONNECTION_TYPES);

export const LINKEDIN_STATUSES = ["OK", "CREDENTIALS", "LISN_CREDENTIALS", "DELETED"] as const;

export const SupportUserSchema = z.object({
	id: z.number(),
	firstName: z.string().nullable().optional(),
	lastName: z.string().nullable().optional(),
	companyId: z.number().nullable().optional(),
	companyName: z.string().nullable().optional(),
	isSendingUser: z.boolean(),
	linkedinConnected: z.boolean(),
	linkedinStatus: z.string().nullable().optional(),
	emailConnected: z.boolean(),
	linkedinConnectionType: LinkedinConnectionTypeSchema,
	/** PREC-4335: the user's last authenticated request, at most an hour stale. */
	lastActiveAt: z.string().nullable().optional(),
});

export const SupportUsersResponseSchema = z.object({
	data: z.array(SupportUserSchema),
	meta: MetaDataSchema,
});

export type ActivityStats = z.infer<typeof ActivityStatsSchema>;
export type FeatureActivityStats = z.infer<typeof FeatureActivityStatsSchema>;
export type SupportActivityWindow = z.infer<typeof SupportActivityWindowSchema>;
export type SubscriptionType = z.infer<typeof SubscriptionTypeSchema>;
export type BillingInterval = z.infer<typeof BillingIntervalSchema>;
export type SupportOpenScrapingRequest = z.infer<typeof SupportOpenScrapingRequestSchema>;
export type SupportRunningCampaign = z.infer<typeof SupportRunningCampaignSchema>;
export type SupportSendErrorsSummary = z.infer<typeof SupportSendErrorsSummarySchema>;
export type SupportSendError = z.infer<typeof SupportSendErrorSchema>;
export type SupportSendErrorsList = z.infer<typeof SupportSendErrorsListSchema>;
export type SupportSendErrorResolved = z.infer<typeof SupportSendErrorResolvedSchema>;
export type SupportSendErrorsOverview = z.infer<typeof SupportSendErrorsOverviewSchema>;
export type SupportCompany = z.infer<typeof SupportCompanySchema>;
export type SupportCompaniesResponse = z.infer<typeof SupportCompaniesResponseSchema>;
export type LinkedinConnectionType = z.infer<typeof LinkedinConnectionTypeSchema>;
export type SupportUser = z.infer<typeof SupportUserSchema>;
export type SupportUsersResponse = z.infer<typeof SupportUsersResponseSchema>;

export interface SupportDashboardParams {
	page: number;
	limit: number;
	searchValue?: string;
	sortBy?: string;
	sortOrder?: "ASC" | "DESC";
	hasOpenScrapingRequest?: string;
	hasRunningCampaign?: string;
	hasSendErrors?: string;
	lowOnCredits?: string;
	sendErrorRange?: SendErrorRange;
}

export interface SupportUsersParams {
	page: number;
	limit: number;
	searchValue?: string;
	sortBy?: string;
	sortOrder?: "ASC" | "DESC";
	linkedinDisconnected?: string;
}
